import { parseTagStringAttribute } from "@utils/parseDynamicUiBody";

/**
 * 重叠分析结果消息的识别与元数据读取（EDG-561 fe-spec §2.4 E-2）。
 *
 * Chat 的 Message 里没有可读的 `referenceType` 或消息类型，前端按「**开标签属性**」约定识别：
 *
 * ```html
 * <dynamic_ui id="etf_overlap_result" metrics='{"fund_count":6,"uncovered_pct":12,"missing_fields":["fees"],"quota_exhausted":false}'>…</dynamic_ui>
 * ```
 *
 * **只在本文件读取该约定**：后端落地的 `id` 前缀 / `metrics` 字段名若不同，只改这里（manifest 与后端核对项）。
 */
export const OVERLAP_RESULT_ID_PREFIX = "etf_overlap_";

export interface OverlapResultMarker {
  fundCount?: number;
  uncoveredPct?: number;
  missingFields?: string[];
  quotaExhausted?: boolean;
}

const OPEN_TAG_PATTERN = /<dynamic_ui\b[^>]*>/g;

const finiteNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const parseMetrics = (raw: string | undefined): OverlapResultMarker => {
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (!parsed || typeof parsed !== "object") {
      return {};
    }
    return {
      fundCount: finiteNumber(parsed.fund_count),
      uncoveredPct: finiteNumber(parsed.uncovered_pct),
      missingFields: Array.isArray(parsed.missing_fields)
        ? parsed.missing_fields.filter(
            (item): item is string => typeof item === "string",
          )
        : undefined,
      quotaExhausted:
        typeof parsed.quota_exhausted === "boolean"
          ? parsed.quota_exhausted
          : undefined,
    };
  } catch {
    return {};
  }
};

/**
 * 正文里有 `id` 以 `etf_overlap_` 开头的 `<dynamic_ui>` → 这是一条重叠分析结果。
 * `metrics` 缺失 / 非法 JSON 时各字段为 `undefined`，**仍视为结果消息**。
 * 非重叠消息返回 `null`；先用 `includes` 短路，不给其它消息增加解析开销。
 */
export const parseOverlapResultMarker = (
  content: string,
): OverlapResultMarker | null => {
  if (!content || !content.includes(OVERLAP_RESULT_ID_PREFIX)) {
    return null;
  }
  for (const tag of content.match(OPEN_TAG_PATTERN) ?? []) {
    const id = parseTagStringAttribute(tag, "id");
    if (id?.startsWith(OVERLAP_RESULT_ID_PREFIX)) {
      return parseMetrics(parseTagStringAttribute(tag, "metrics"));
    }
  }
  return null;
};

/** ETF 不足消息（SC-13）没有 `<dynamic_ui>`，以其下挂的连账户卡标题区别于结果页的 `Add the rest of your funds`。 */
export const OVERLAP_INSUFFICIENT_CARD_TITLE = "Add your other funds";

export const isOverlapInsufficientCard = (title?: string): boolean =>
  title === OVERLAP_INSUFFICIENT_CARD_TITLE;

/** ETF 不足消息说明文案里的 `I found {n}`；取不到返回 `undefined`（调用方省略该参数）。 */
export const parseInsufficientEtfCount = (
  content: string,
): number | undefined => {
  const matched = content.match(/I found (\d+)/);
  return matched ? Number(matched[1]) : undefined;
};
